import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { fuzzyMatch } from '../org-filter-utils';

export interface PositionSearchResult {
    term: string;
    matchedNames: string[];
}

@Component({
    selector: 'shared-org-search-bar',
    imports: [FormsModule, MatFormFieldModule, MatIconModule, MatInputModule, MatButtonModule],
    template: `
        <div class="search-container">
            <div class="search-row">
                <mat-form-field appearance="outline" class="flex-1-auto" subscriptSizing="dynamic">
                    <mat-label>{{ searchLabel }}</mat-label>
                    <input
                        matInput
                        [(ngModel)]="searchTerm"
                        (ngModelChange)="onSearchChange()"
                        [placeholder]="searchPlaceholder"
                    />
                    @if (!searchTerm) {
                        <mat-icon matSuffix>search</mat-icon>
                    }
                    @if (searchTerm) {
                        <button matSuffix mat-icon-button (click)="clearSearch()">
                            <mat-icon>clear</mat-icon>
                        </button>
                    }
                </mat-form-field>

                @if (showPositionSearch) {
                    <!-- NOTE: Using <mat-icon inline> inside <mat-label> instead of matPrefix.
                         matPrefix breaks when this component is rendered inside a hidden mat-tab
                         (e.g. new-hat dialog "Existing Hats" tab). Angular Material calculates
                         the prefix icon width at init time — inside a hidden tab that's 0px,
                         so the label's translateX offset is never set and "Filter " gets clipped
                         behind the icon. The inline icon scales with the label text and needs
                         no runtime dimension calculations. -->
                    <mat-form-field appearance="outline" class="flex-1-auto" subscriptSizing="dynamic">
                        <mat-label><mat-icon inline>account_tree</mat-icon> Filter by org part</mat-label>
                        <input
                            matInput
                            [(ngModel)]="positionTerm"
                            (ngModelChange)="onPositionChange()"
                            placeholder="Type an org part name..."
                        />
                        @if (!positionTerm) {
                            <mat-icon matSuffix>search</mat-icon>
                        }
                        @if (positionTerm) {
                            <button matSuffix mat-icon-button (click)="clearPositionSearch()">
                                <mat-icon>clear</mat-icon>
                            </button>
                        }
                    </mat-form-field>
                }
            </div>

            @if (showPositionSearch && positionTerm) {
                @if (matchedPositionNames.length > 0) {
                    <div class="position-match-info">
                        <mat-icon class="info-icon">info_outline</mat-icon>
                        Showing items linked to:
                        @for (pos of matchedPositionNames; track pos) {
                            <span class="position-chip"> <mat-icon class="chip-icon">work</mat-icon>{{ pos }} </span>
                        }
                    </div>
                } @else {
                    <div class="position-match-info no-match">
                        <mat-icon class="info-icon">warning</mat-icon>
                        No org parts matching "{{ positionTerm }}"
                    </div>
                }
            }
        </div>
    `,
    styles: [
        `
            :host {
                flex: 1;
                min-width: 0;
            }

            .search-container {
                padding-top: 8px;
            }

            .search-row {
                display: flex;
                gap: 12px;
            }

            .flex-1-auto {
                flex: 1 1 auto;
            }

            /* Phones: stack the search + org-part fields so neither squashes and the
           suffix search icon never overlaps the (otherwise clipped) label. */
            @media (max-width: 767.98px) {
                .search-row {
                    flex-direction: column;
                    gap: 4px;
                }
            }

            .position-match-info {
                display: flex;
                align-items: center;
                gap: 6px;
                flex-wrap: wrap;
                font-size: 13px;
                color: rgba(0, 0, 0, 0.6);
                margin-top: 4px;
                margin-bottom: 8px;
                padding: 6px 12px;
                background: rgba(33, 150, 243, 0.06);
                border-radius: 8px;
                border-left: 3px solid #2196f3;

                &.no-match {
                    background: rgba(255, 152, 0, 0.06);
                    border-left-color: #ff9800;
                    color: rgba(0, 0, 0, 0.5);
                }

                .info-icon {
                    font-size: 16px;
                    width: 16px;
                    height: 16px;
                }
            }

            .position-chip {
                display: inline-flex;
                align-items: center;
                gap: 3px;
                background: rgba(33, 150, 243, 0.12);
                color: #1565c0;
                padding: 2px 8px;
                border-radius: 12px;
                font-weight: 500;
                font-size: 12px;

                .chip-icon {
                    font-size: 14px;
                    width: 14px;
                    height: 14px;
                }
            }

            :host-context(.dark-theme) .position-match-info {
                background: rgba(33, 150, 243, 0.1);
                color: rgba(255, 255, 255, 0.7);

                &.no-match {
                    background: rgba(255, 152, 0, 0.1);
                }
            }

            :host-context(.dark-theme) .position-chip {
                background: rgba(33, 150, 243, 0.2);
                color: #64b5f6;
            }
        `,
    ],
})
export class OrgSearchBarComponent implements OnInit {
    /** Label for the main search input. */
    @Input() searchLabel = 'Search';

    /** Placeholder for the main search input. */
    @Input() searchPlaceholder = 'Search...';

    /** Show the optional position search field. */
    @Input() showPositionSearch = false;

    /** All available position names for fuzzy matching. */
    @Input() positionNames: string[] = [];

    /** Optional initial value for the position search field. */
    @Input() initialPositionTerm = '';

    /** Emits the search term whenever it changes. */
    @Output() searchChanged = new EventEmitter<string>();

    /** Emits position search result (term + matched position names). */
    @Output() positionSearchChanged = new EventEmitter<PositionSearchResult>();

    searchTerm = '';
    positionTerm = '';
    matchedPositionNames: string[] = [];

    ngOnInit(): void {
        if (this.initialPositionTerm) {
            this.positionTerm = this.initialPositionTerm;
            this.onPositionChange();
        }
    }

    onSearchChange(): void {
        this.searchChanged.emit(this.searchTerm);
    }

    onPositionChange(): void {
        if (this.positionTerm.trim()) {
            const term = this.positionTerm.toLowerCase().trim();
            this.matchedPositionNames = this.positionNames.filter((name) => fuzzyMatch(name.toLowerCase(), term));
        } else {
            this.matchedPositionNames = [];
        }
        this.positionSearchChanged.emit({
            term: this.positionTerm,
            matchedNames: this.matchedPositionNames,
        });
    }

    clearSearch(): void {
        this.searchTerm = '';
        this.searchChanged.emit('');
    }

    clearPositionSearch(): void {
        this.positionTerm = '';
        this.matchedPositionNames = [];
        this.positionSearchChanged.emit({ term: '', matchedNames: [] });
    }
}
